import { Trophy, CheckCircle, IndianRupee } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  DataTable,
  PageHeader,
  useDataTable,
  type DataTableColumn,
  CompactStatCard,
  StatsGrid,
} from "@/components/shared";
import { useAdminRewardRequests } from "@/hooks/queries";
import type { RewardRequestDto } from "@/types/rank-reward.dto";
import { Skeleton } from "@/components/ui/skeleton";

const formatDate = (value?: string) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const columns: DataTableColumn<RewardRequestDto>[] = [
  {
    id: "userName",
    header: "User",
    accessorKey: "userName",
    sortable: true,
    mobilePriority: 1,
    cell: (row) => (
      <div>
        <p className="font-semibold text-sm">{row.userName}</p>
        <p className="text-xs text-muted-foreground">{row.userEmail}</p>
      </div>
    ),
  },
  {
    id: "rank",
    header: "Rank Achieved",
    accessorKey: "rank",
    sortable: true,
    mobilePriority: 2,
    cell: (row) => (
      <div className="flex items-center gap-2">
        <Trophy className="h-4 w-4 text-yellow-500" />
        <span className="font-medium">{row.rank}</span>
      </div>
    ),
  },
  {
    id: "rewardAmount",
    header: "Reward Amount",
    accessorKey: "rewardAmount",
    sortable: true,
    align: "right",
    mobileLabel: "Reward",
    mobilePriority: 3,
    cell: (row) => (
      <span className="font-bold text-green-600">₹{row.rewardAmount.toLocaleString()}</span>
    ),
  },
  {
    id: "claimedAt",
    header: "Claim Date",
    accessorKey: "claimedAt",
    sortable: true,
    mobilePriority: 4,
    cell: (row) => (
      <span className="text-sm text-muted-foreground">
        {formatDate(row.claimedAt || row.achievedAt)}
      </span>
    ),
  },
  {
    id: "status",
    header: "Status",
    cell: () => (
      <Badge className="bg-green-100 text-green-700 border-green-300 gap-1 hover:bg-green-100">
        <CheckCircle className="h-3 w-3" />
        Completed
      </Badge>
    ),
  },
];

const AdminRewardRequests = () => {
  const { data: requests = [], isLoading } = useAdminRewardRequests();

  // Filter only completed (claimed) rewards
  const completedRewards = requests.filter((r) => r.isClaimed);

  const table = useDataTable<RewardRequestDto>({
    data: completedRewards,
    searchKeys: ["userName", "userEmail", "rank"],
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Completed Rewards"
          description="View all completed and claimed rank rewards."
        />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  const totalDisbursed = completedRewards.reduce((sum, r) => sum + r.rewardAmount, 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Completed Rewards"
        description="View claimed rank rewards history with immediate wallet payout status."
      />

      <StatsGrid columns={2}>
        <CompactStatCard
          title="Total Claims Completed"
          value={String(completedRewards.length)}
          icon={Trophy}
          iconClassName="text-yellow-500 bg-yellow-100"
        />
        <CompactStatCard
          title="Total Reward Disbursed"
          value={`₹${totalDisbursed.toLocaleString()}`}
          icon={IndianRupee}
          iconClassName="text-green-500 bg-green-100"
        />
      </StatsGrid>

      <DataTable
        title="Completed Reward History"
        columns={columns}
        data={table.rows}
        getRowId={(row) => String(row.id)}
        search={table.state.search}
        onSearchChange={table.setSearch}
        searchPlaceholder="Search by user, email or rank..."
        sort={table.state.sort}
        onSortChange={table.toggleSort}
        pagination={table.pagination}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        emptyMessage="No completed reward claims yet."
        renderMobileCard={(row) => (
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div>
              <p className="font-semibold text-sm">{row.userName}</p>
              <div className="flex items-center gap-1 mt-0.5">
                <Trophy className="h-3.5 w-3.5 text-yellow-500" />
                <p className="text-xs text-muted-foreground">{row.rank}</p>
              </div>
              <p className="text-xs text-muted-foreground">
                Claimed: {formatDate(row.claimedAt || row.achievedAt)}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <span className="font-bold text-green-600 text-sm">
                ₹{row.rewardAmount.toLocaleString()}
              </span>
              <Badge className="bg-green-100 text-green-700 border-green-300 text-xs">
                Completed
              </Badge>
            </div>
          </div>
        )}
      />
    </div>
  );
};

export default AdminRewardRequests;
